/**
 * NetworksCompare — `/networks/compare`
 *
 * Cross-chain catalog of every network the app monitors (Algorand,
 * Axelar, Cardano, Cronos, Hedera, ICP, TheGraph, Vechain, XDC).
 *
 * **Phase 1 (this commit)** — static metadata catalog: name, native
 * token, staking model, indicative APY, "Open Monitor" CTA. Lets a
 * user discover the available monitors at a glance + jump straight
 * into the per-chain page. No per-chain data fetching here, so the
 * page renders instantly with zero network cost.
 *
 * **Phase 2 (follow-up)** — wire live user totals (n wallets, total
 * balance, USD value) by calling the per-chain `useXxxMonitorWallets`
 * + `useXxxWallets` hooks once the aggregation hook is extracted.
 * Tracked as **NETWORKS_COMPARE_PHASE_2** in ROADMAP_2026.
 *
 * Search + filter on the staking model (PoS, masternode, neuron,
 * indexer, validator-as-a-service) live in client memory only.
 */

import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Icons } from '../components/Icons';
import {
  useNetworksOverview,
  type NetworkOverviewKey,
} from '../hooks/useNetworksOverview';
import { BalanceCell } from '../components/networks-compare/BalanceCells';

// --- Catalog -----------------------------------------------------------

type StakingModel =
  | 'pos-delegation'
  | 'masternode'
  | 'neuron'
  | 'indexer-delegation'
  | 'validator-staking'
  | 'staking-pool'
  | 'native-staking';

interface NetworkRow {
  /** Sidebar page key (matches the monitor route). */
  pageKey: NetworkOverviewKey;
  /** Chain display name. */
  name: string;
  /** Native token ticker. */
  ticker: string;
  /** Staking model (drives the filter). */
  stakingModel: StakingModel;
  /** Indicative APY as displayed on the chain's monitor page. Range
   *  string because some chains expose a band rather than a fixed value. */
  indicativeApy: string;
  /** Brand colour used to tint the chain badge. */
  brand: string;
  /** Short one-liner describing what the chain does in this app. */
  blurb: string;
}

const NETWORKS: NetworkRow[] = [
  {
    pageKey: 'algorand-monitor',
    name: 'Algorand',
    ticker: 'ALGO',
    stakingModel: 'pos-delegation',
    indicativeApy: '~5%',
    brand: '#000000',
    blurb: 'Pure PoS chain — automatic governance rewards on any wallet balance.',
  },
  {
    pageKey: 'axelar-monitor',
    name: 'Axelar',
    ticker: 'AXL',
    stakingModel: 'validator-staking',
    indicativeApy: '~10–14%',
    brand: '#5A5A8E',
    blurb: 'Cross-chain hub — validator delegation + cross-chain transfer fees.',
  },
  {
    pageKey: 'cardano-monitor',
    name: 'Cardano',
    ticker: 'ADA',
    stakingModel: 'staking-pool',
    indicativeApy: '~3–5%',
    brand: '#0033AD',
    blurb: 'Stake-pool delegation — non-custodial, per-epoch rewards.',
  },
  {
    pageKey: 'cronos-monitor',
    name: 'Cronos POS',
    ticker: 'CRO',
    stakingModel: 'pos-delegation',
    indicativeApy: '~7.4%',
    brand: '#1199FA',
    blurb: 'Cronos POS chain — validator delegation + no-loss lottery integration.',
  },
  {
    pageKey: 'hedera-monitor',
    name: 'Hedera',
    ticker: 'HBAR',
    stakingModel: 'native-staking',
    indicativeApy: '~6.5%',
    brand: '#000000',
    blurb: 'Native account staking — automatic rewards per epoch + token tracking.',
  },
  {
    pageKey: 'icp-monitor',
    name: 'Internet Computer',
    ticker: 'ICP',
    stakingModel: 'neuron',
    indicativeApy: '~7–18%',
    brand: '#29ABE2',
    blurb: 'Neuron-based governance staking — APY scales with dissolve delay.',
  },
  {
    pageKey: 'thegraph-monitor',
    name: 'The Graph',
    ticker: 'GRT',
    stakingModel: 'indexer-delegation',
    indicativeApy: '~6–9%',
    brand: '#5C5DDD',
    blurb: 'Delegate to indexers — query fee + inflation rewards on Arbitrum.',
  },
  {
    pageKey: 'vechain-monitor',
    name: 'Vechain',
    ticker: 'VET',
    stakingModel: 'masternode',
    indicativeApy: '~1–2%',
    brand: '#15BDFF',
    blurb: 'Authority masternode rewards — VTHO emission + per-block VET stake.',
  },
  {
    pageKey: 'xdc-monitor',
    name: 'XDC Network',
    ticker: 'XDC',
    stakingModel: 'masternode',
    indicativeApy: '~8–12%',
    brand: '#244A87',
    blurb: 'XDC Masternode delegation — block rewards + token tracking.',
  },
];

const STAKING_MODEL_LABEL: Record<StakingModel, string> = {
  'pos-delegation': 'PoS Delegation',
  masternode: 'Masternode',
  neuron: 'Neuron',
  'indexer-delegation': 'Indexer Delegation',
  'validator-staking': 'Validator Staking',
  'staking-pool': 'Staking Pool',
  'native-staking': 'Native Staking',
};

// --- Page --------------------------------------------------------------

interface NetworksCompareProps {
  /** Imperative navigation callback (Sidebar / App router). The page
   *  doesn't know about the routing implementation, just emits the
   *  target page key. */
  onNavigate?: (pageKey: string) => void;
}

export function NetworksCompare({ onNavigate }: NetworksCompareProps = {}) {
  const { t } = useTranslation();
  const [search, setSearch] = useState('');
  const [modelFilter, setModelFilter] = useState<StakingModel | 'all'>('all');
  const [onlyConfigured, setOnlyConfigured] = useState(false);
  // Phase 3: gate live-balance fetching behind an opt-in toggle so
  // visitors who just want the catalog don't pay for 8 chain APIs on
  // mount. Once enabled, each <BalanceCell> mounts its own data hook.
  const [loadBalances, setLoadBalances] = useState(false);
  const overview = useNetworksOverview();

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return NETWORKS.filter((n) => {
      if (modelFilter !== 'all' && n.stakingModel !== modelFilter) return false;
      if (onlyConfigured && (overview[n.pageKey]?.walletsCount ?? 0) === 0) return false;
      if (!q) return true;
      return (
        n.name.toLowerCase().includes(q) ||
        n.ticker.toLowerCase().includes(q) ||
        n.blurb.toLowerCase().includes(q)
      );
    });
  }, [search, modelFilter, onlyConfigured, overview]);

  const totalWallets = useMemo(
    () => Object.values(overview).reduce((sum, n) => sum + n.walletsCount, 0),
    [overview],
  );
  const chainsWithWallets = useMemo(
    () => Object.values(overview).filter((n) => n.walletsCount > 0).length,
    [overview],
  );

  return (
    <div style={{ padding: '0', maxWidth: '1400px', margin: '0 auto', paddingBottom: '40px' }}>
      <header
        style={{
          display: 'flex',
          alignItems: 'flex-start',
          gap: '16px',
          marginBottom: '24px',
          flexWrap: 'wrap',
        }}
      >
        <div
          style={{
            width: '52px',
            height: '52px',
            borderRadius: '14px',
            background: 'linear-gradient(135deg, #0EA5E9, #6366F1)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}
          aria-hidden
        >
          <Icons.Globe size={28} color="white" />
        </div>
        <div style={{ flex: 1, minWidth: '280px' }}>
          <h1 style={{ fontSize: '24px', fontWeight: 700, margin: 0 }}>
            {t('networks.compare.title', 'Networks — Compare')}
          </h1>
          <p style={{ fontSize: '13px', color: 'var(--muted)', marginTop: '4px' }}>
            {t(
              'networks.compare.subtitle',
              'Side-by-side overview of every chain monitored. Search, filter by staking model, then jump into the dedicated monitor.',
            )}
          </p>
        </div>
      </header>

      <section
        aria-label={t('networks.compare.controls', 'Search and filter')}
        style={{
          background: 'rgba(0, 0, 0, 0.2)',
          border: '1px solid rgba(255, 255, 255, 0.08)',
          borderRadius: '14px',
          padding: '16px',
          marginBottom: '20px',
          display: 'flex',
          gap: '12px',
          flexWrap: 'wrap',
        }}
      >
        <label style={{ flex: 1, minWidth: '240px', display: 'flex', flexDirection: 'column', gap: '6px' }}>
          <span style={{ fontSize: '12px', color: 'var(--muted)' }}>
            {t('networks.compare.search', 'Search')}
          </span>
          <input
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={t('networks.compare.searchPlaceholder', 'Algorand, ALGO, masternode…')}
            style={{
              padding: '10px 12px',
              background: 'rgba(0, 0, 0, 0.3)',
              border: '1px solid rgba(255, 255, 255, 0.15)',
              borderRadius: '10px',
              color: 'var(--text)',
              fontSize: '13px',
              outline: 'none',
            }}
          />
        </label>
        <label style={{ minWidth: '200px', display: 'flex', flexDirection: 'column', gap: '6px' }}>
          <span style={{ fontSize: '12px', color: 'var(--muted)' }}>
            {t('networks.compare.modelFilter', 'Staking model')}
          </span>
          <select
            value={modelFilter}
            onChange={(e) => setModelFilter(e.target.value as StakingModel | 'all')}
            style={{
              padding: '10px 12px',
              background: 'rgba(0, 0, 0, 0.3)',
              border: '1px solid rgba(255, 255, 255, 0.15)',
              borderRadius: '10px',
              color: 'var(--text)',
              fontSize: '13px',
              outline: 'none',
            }}
          >
            <option value="all">{t('networks.compare.modelAll', 'All')}</option>
            {Object.entries(STAKING_MODEL_LABEL).map(([k, label]) => (
              <option key={k} value={k}>
                {label}
              </option>
            ))}
          </select>
        </label>
        <label
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            paddingTop: '20px',
            fontSize: '13px',
            color: 'var(--text)',
            cursor: 'pointer',
          }}
        >
          <input
            type="checkbox"
            checked={onlyConfigured}
            onChange={(e) => setOnlyConfigured(e.target.checked)}
          />
          <span>
            {t('networks.compare.onlyConfigured', 'Only chains I have wallets on')}
          </span>
        </label>
        {!loadBalances ? (
          <button
            type="button"
            onClick={() => setLoadBalances(true)}
            disabled={chainsWithWallets === 0}
            style={{
              marginLeft: 'auto',
              padding: '10px 16px',
              background: chainsWithWallets > 0
                ? 'linear-gradient(135deg, #0EA5E9, #6366F1)'
                : 'rgba(255, 255, 255, 0.05)',
              color: chainsWithWallets > 0 ? 'white' : 'var(--muted)',
              border: '1px solid rgba(99, 102, 241, 0.3)',
              borderRadius: '10px',
              fontSize: '13px',
              fontWeight: 600,
              cursor: chainsWithWallets > 0 ? 'pointer' : 'not-allowed',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
            }}
            title={
              chainsWithWallets === 0
                ? t('networks.compare.loadBalancesDisabled', 'Add wallets in any chain monitor first')
                : t('networks.compare.loadBalancesHint', 'Fetch live USD totals from each chain (one API call per configured chain)')
            }
          >
            <Icons.RefreshCw size={14} />
            {t('networks.compare.loadBalances', 'Load live balances')}
          </button>
        ) : (
          <button
            type="button"
            onClick={() => setLoadBalances(false)}
            style={{
              marginLeft: 'auto',
              padding: '10px 16px',
              background: 'rgba(255, 255, 255, 0.08)',
              color: 'var(--text)',
              border: '1px solid rgba(255, 255, 255, 0.15)',
              borderRadius: '10px',
              fontSize: '13px',
              fontWeight: 600,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
            }}
          >
            <Icons.X size={14} />
            {t('networks.compare.unloadBalances', 'Hide live balances')}
          </button>
        )}
      </section>

      <p
        style={{
          fontSize: '12px',
          color: 'var(--muted)',
          marginBottom: '12px',
        }}
      >
        {t(
          'networks.compare.summary',
          '{{configured}} of {{total}} chains configured · {{wallets}} wallets across all chains',
          {
            configured: chainsWithWallets,
            total: Object.keys(overview).length,
            wallets: totalWallets,
          },
        )}
      </p>

      {filtered.length === 0 ? (
        <p
          style={{
            background: 'rgba(0, 0, 0, 0.2)',
            border: '1px solid rgba(255, 255, 255, 0.08)',
            borderRadius: '14px',
            padding: '40px 20px',
            textAlign: 'center',
            color: 'var(--muted)',
          }}
        >
          {t('networks.compare.empty', 'No chain matches your search.')}
        </p>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table
            style={{
              width: '100%',
              borderCollapse: 'collapse',
              background: 'rgba(0, 0, 0, 0.2)',
              border: '1px solid rgba(255, 255, 255, 0.08)',
              borderRadius: '14px',
              overflow: 'hidden',
            }}
          >
            <caption
              style={{
                position: 'absolute',
                width: '1px',
                height: '1px',
                padding: 0,
                margin: '-1px',
                overflow: 'hidden',
                clip: 'rect(0,0,0,0)',
                whiteSpace: 'nowrap',
                border: 0,
              }}
            >
              {t(
                'networks.compare.tableCaption',
                'Comparison of every monitored network: name, native token, staking model, indicative APY.',
              )}
            </caption>
            <thead>
              <tr style={{ background: 'rgba(255, 255, 255, 0.04)' }}>
                <th scope="col" style={thStyle}>
                  {t('networks.compare.colNetwork', 'Network')}
                </th>
                <th scope="col" style={thStyle}>
                  {t('networks.compare.colToken', 'Token')}
                </th>
                <th scope="col" style={thStyle}>
                  {t('networks.compare.colModel', 'Model')}
                </th>
                <th scope="col" style={thStyle}>
                  {t('networks.compare.colApy', 'Indicative APY')}
                </th>
                <th scope="col" style={{ ...thStyle, textAlign: 'right' }}>
                  {t('networks.compare.colWallets', 'My Wallets')}
                </th>
                {loadBalances && (
                  <th scope="col" style={{ ...thStyle, textAlign: 'right' }}>
                    {t('networks.compare.colLiveValue', 'Live Value')}
                  </th>
                )}
                <th scope="col" style={thStyle}>
                  <span className="sr-only">
                    {t('networks.compare.colAction', 'Open Monitor')}
                  </span>
                </th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((row) => (
                <tr key={row.pageKey} style={{ borderTop: '1px solid rgba(255, 255, 255, 0.06)' }}>
                  <th scope="row" style={{ ...tdStyle, textAlign: 'left', fontWeight: 600 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                      <span
                        aria-hidden
                        style={{
                          width: '10px',
                          height: '10px',
                          borderRadius: '50%',
                          background: row.brand,
                          display: 'inline-block',
                          flexShrink: 0,
                        }}
                      />
                      <span>{row.name}</span>
                    </div>
                    <p
                      style={{
                        fontSize: '11px',
                        color: 'var(--muted)',
                        margin: '4px 0 0 20px',
                        fontWeight: 400,
                        lineHeight: 1.4,
                      }}
                    >
                      {row.blurb}
                    </p>
                  </th>
                  <td style={tdStyle}>
                    <code style={{ fontSize: '12px' }}>{row.ticker}</code>
                  </td>
                  <td style={tdStyle}>{STAKING_MODEL_LABEL[row.stakingModel]}</td>
                  <td style={tdStyle}>{row.indicativeApy}</td>
                  <td style={{ ...tdStyle, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                    {(() => {
                      const count = overview[row.pageKey]?.walletsCount ?? 0;
                      return count > 0 ? (
                        <span
                          style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '6px',
                            padding: '2px 10px',
                            background: 'rgba(16, 185, 129, 0.12)',
                            color: '#34D399',
                            border: '1px solid rgba(16, 185, 129, 0.25)',
                            borderRadius: '999px',
                            fontWeight: 600,
                          }}
                        >
                          {count}
                        </span>
                      ) : (
                        <span style={{ color: 'var(--muted)' }}>—</span>
                      );
                    })()}
                  </td>
                  {loadBalances && (
                    <td style={{ ...tdStyle, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                      <BalanceCell
                        pageKey={row.pageKey}
                        wallets={overview[row.pageKey]?.wallets ?? []}
                      />
                    </td>
                  )}
                  <td style={{ ...tdStyle, textAlign: 'right' }}>
                    <button
                      onClick={() => onNavigate?.(row.pageKey)}
                      disabled={!onNavigate}
                      style={{
                        padding: '8px 14px',
                        background: 'rgba(99, 102, 241, 0.15)',
                        color: '#A5B4FC',
                        border: '1px solid rgba(99, 102, 241, 0.3)',
                        borderRadius: '8px',
                        fontSize: '12px',
                        fontWeight: 600,
                        cursor: onNavigate ? 'pointer' : 'not-allowed',
                        opacity: onNavigate ? 1 : 0.5,
                      }}
                    >
                      {t('networks.compare.openMonitor', 'Open Monitor')}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

const thStyle: React.CSSProperties = {
  textAlign: 'left',
  padding: '12px 16px',
  fontSize: '12px',
  fontWeight: 600,
  color: 'var(--muted)',
  textTransform: 'uppercase',
  letterSpacing: '0.5px',
};

const tdStyle: React.CSSProperties = {
  padding: '14px 16px',
  fontSize: '13px',
  color: 'var(--text)',
  verticalAlign: 'top',
};

export default NetworksCompare;
